"use client";

import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import type { Atom, Cursor, Mode } from "./calc/types";
import { newId } from "./calc/types";
import { useToolLock } from "@/lib/ToolLockContext";
import { useCalculator } from "@/components/calculator/CalculatorContext";
import MultiplicationTable from "./MultiplicationTable";
import {
  backspace,
  cursorAtEnd,
  insertAt,
  insertDecimal,
  insertDigit,
  insertGroup,
  moveLeft,
  moveRight,
} from "./calc/manipulate";
import { RenderList } from "./calc/render";
import { CalcError, evalRoot, formatNumber } from "./calc/evaluate";
import type { MaId } from "@/lib/formelblad-shared";

interface CalcState {
  expr: Atom[];
  cursor: Cursor;
  result: string | null; // visas under uttrycket; rensas vid ny inmatning
  error: string | null;
  mode: Mode;
}

type Act =
  | { type: "digit"; v: string }
  | { type: "decimal" }
  | { type: "op"; v: "+" | "-" | "*" | "/" }
  | { type: "const"; name: "pi" | "e" }
  | { type: "lparen" }
  | { type: "rparen" }
  | { type: "comma" }
  | { type: "sup" }
  | { type: "square" }
  | { type: "sqrt" }
  | { type: "nthroot" }
  | { type: "frac" }
  | { type: "fn"; name: "sin" | "cos" | "tan" | "asin" | "acos" | "atan" | "ln" | "lg" | "log" }
  | { type: "abs" }
  | { type: "logbase" }
  | { type: "expY" }
  | { type: "tenY" }
  | { type: "recip" }
  | { type: "fact" }
  | { type: "ncr" }
  | { type: "npr" }
  | { type: "backspace" }
  | { type: "clear" }
  | { type: "equals" }
  | { type: "cursor"; cursor: Cursor }
  | { type: "arrow-left" }
  | { type: "arrow-right" }
  | { type: "toggleMode" };

function initial(): CalcState {
  const expr: Atom[] = [];
  return {
    expr,
    cursor: cursorAtEnd(expr),
    result: null,
    error: null,
    mode: "deg",
  };
}

function reducer(state: CalcState, act: Act): CalcState {
  // Vid ny inmatning efter resultat — rensa resultat-displayen men behåll uttrycket
  const clearResult = (s: CalcState) => ({ ...s, result: null, error: null });

  switch (act.type) {
    case "digit": {
      const r = insertDigit(state.expr, state.cursor, act.v);
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "decimal": {
      const r = insertDecimal(state.expr, state.cursor);
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "op": {
      const r = insertAt(state.expr, state.cursor, {
        id: newId(),
        kind: "op",
        v: act.v,
      });
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "const": {
      const r = insertAt(state.expr, state.cursor, {
        id: newId(),
        kind: "const",
        name: act.name,
      });
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "lparen": {
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "paren", inner: [] },
        null,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "rparen": {
      // Hoppa ut ur nuvarande slot oavsett typ. Sätter cursor till
      // positionen direkt efter containern i parent-listan.
      if (state.cursor.path.length === 0) return state;
      const containerSeg = state.cursor.path[state.cursor.path.length - 1];
      const containerId = containerSeg.split(":")[0];
      const parentPath = state.cursor.path.slice(0, -1);
      const parentList = listAtPath(state.expr, parentPath);
      if (!parentList) return state;
      const idx = parentList.findIndex((a) => a.id === containerId);
      if (idx < 0) return state;
      return clearResult({
        ...state,
        cursor: { path: parentPath, index: idx + 1 },
      });
    }
    case "comma": {
      const r = insertAt(state.expr, state.cursor, {
        id: newId(),
        kind: "comma",
      });
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "sup": {
      // Om föregående atom är ett operand-värde (num/const/paren/sqrt/sup/fn),
      // använd det som base och placera cursor i exponent-slotsen.
      const id = newId();
      const cur = state.cursor;
      const list = listAtPath(state.expr, cur.path);
      if (list && cur.index > 0) {
        const prev = list[cur.index - 1];
        const usableAsBase =
          prev.kind === "num" ||
          prev.kind === "const" ||
          prev.kind === "paren" ||
          prev.kind === "abs" ||
          prev.kind === "sqrt" ||
          prev.kind === "fn";
        if (usableAsBase) {
          const newRoot = withListAtPath(state.expr, cur.path, (l) => {
            const next = l.slice();
            next[cur.index - 1] = {
              id,
              kind: "sup",
              base: [next[cur.index - 1]],
              exp: [],
            };
            return next;
          });
          // Cursor i exp-slotsen för det nya sup
          return clearResult({
            ...state,
            expr: newRoot,
            cursor: { path: [...cur.path, `${id}:1`], index: 0 },
          });
        }
      }
      // Tom base — cursor i base först (måste fyllas i)
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "sup", base: [], exp: [] },
        0,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "square": {
      // x² — ta föregående atom (om operand) och slå in i sup{base, exp=[2]}
      // Annars: tom base
      const id = newId();
      const expAtoms: Atom[] = [{ id: newId(), kind: "num", v: "2" }];
      // Försök använda föregående atom som base
      // För enkelhet: bara hantera fallet "föregående är operand i samma list"
      // Annars sätt tom base
      const cur = state.cursor;
      const list = listAtPath(state.expr, cur.path);
      if (list && cur.index > 0) {
        const prev = list[cur.index - 1];
        if (
          prev.kind === "num" ||
          prev.kind === "const" ||
          prev.kind === "paren"
        ) {
          // Ersätt prev med sup{prev}^{2}
          const newRoot = withListAtPath(state.expr, cur.path, (l) => {
            const next = l.slice();
            next[cur.index - 1] = {
              id,
              kind: "sup",
              base: [next[cur.index - 1]],
              exp: expAtoms,
            };
            return next;
          });
          return clearResult({ ...state, expr: newRoot });
        }
      }
      // Tom base
      const r = insertAt(state.expr, state.cursor, {
        id,
        kind: "sup",
        base: [],
        exp: expAtoms,
      });
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "sqrt": {
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "sqrt", inner: [] },
        null,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "nthroot": {
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "nthroot", degree: [], inner: [] },
        0,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "frac": {
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "frac", num: [], den: [] },
        0,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "fn": {
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "fn", name: act.name, arg: [] },
        0,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "abs": {
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "abs", inner: [] },
        null,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "logbase": {
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "logbase", base: [], arg: [] },
        0,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "expY": {
      // e^y — infoga sup{base=[const e], exp=[]} med cursor i exp-slot
      const eId = newId();
      const supId = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        {
          id: supId,
          kind: "sup",
          base: [{ id: eId, kind: "const", name: "e" }],
          exp: [],
        },
        1,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "tenY": {
      // 10^y — infoga sup{base=[10], exp=[]} med cursor i exp-slot
      const tenId = newId();
      const supId = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        {
          id: supId,
          kind: "sup",
          base: [{ id: tenId, kind: "num", v: "10" }],
          exp: [],
        },
        1,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "recip": {
      // 1/x — infoga frac{num=[1], den=[]} med cursor i den-slot
      const oneId = newId();
      const fracId = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        {
          id: fracId,
          kind: "frac",
          num: [{ id: oneId, kind: "num", v: "1" }],
          den: [],
        },
        1,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "fact": {
      // n! — wrappa föregående operand i fact{}, annars fact{} med tom inner
      const id = newId();
      const cur = state.cursor;
      const list = listAtPath(state.expr, cur.path);
      if (list && cur.index > 0) {
        const prev = list[cur.index - 1];
        const wrappable =
          prev.kind === "num" ||
          prev.kind === "const" ||
          prev.kind === "paren";
        if (wrappable) {
          const newRoot = withListAtPath(state.expr, cur.path, (l) => {
            const next = l.slice();
            next[cur.index - 1] = { id, kind: "fact", inner: [next[cur.index - 1]] };
            return next;
          });
          return clearResult({ ...state, expr: newRoot });
        }
      }
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "fact", inner: [] },
        null,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "ncr": {
      // C(n, r) — infoga binom med tomma slots, cursor i n
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "binom", n: [], r: [], binomTyp: "ncr" },
        0,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "npr": {
      // P(n, r) — infoga binom med tomma slots, cursor i n
      const id = newId();
      const r = insertGroup(
        state.expr,
        state.cursor,
        { id, kind: "binom", n: [], r: [], binomTyp: "npr" },
        0,
      );
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "backspace": {
      const r = backspace(state.expr, state.cursor);
      return clearResult({ ...state, expr: r.root, cursor: r.cursor });
    }
    case "clear":
      return initial();
    case "equals": {
      try {
        const v = evalRoot(state.expr, state.mode);
        return { ...state, result: formatNumber(v), error: null };
      } catch (err) {
        const msg = err instanceof CalcError ? err.message : "ogiltigt uttryck";
        return { ...state, result: null, error: msg };
      }
    }
    case "cursor":
      return clearResult({ ...state, cursor: act.cursor });
    case "arrow-left":
      return clearResult({ ...state, cursor: moveLeft(state.expr, state.cursor) });
    case "arrow-right":
      return clearResult({ ...state, cursor: moveRight(state.expr, state.cursor) });
    case "toggleMode":
      return { ...state, mode: state.mode === "deg" ? "rad" : "deg" };
  }
}

// Hjälp-helpers (importerade från manipulate via re-export hade gett cykel — inline)
function listAtPath(root: Atom[], path: string[]): Atom[] | null {
  let list: Atom[] = root;
  for (const seg of path) {
    const [id, slotStr] = seg.split(":");
    const slot = slotStr === undefined ? null : (Number(slotStr) as 0 | 1);
    const node = list.find((a) => a.id === id);
    if (!node) return null;
    const next = childList(node, slot);
    if (!next) return null;
    list = next;
  }
  return list;
}

function childList(a: Atom, slot: 0 | 1 | null): Atom[] | null {
  switch (a.kind) {
    case "paren":
    case "abs":
    case "sqrt":
      return slot === null ? a.inner : null;
    case "nthroot":
      return slot === 0 ? a.degree : slot === 1 ? a.inner : null;
    case "frac":
      return slot === 0 ? a.num : slot === 1 ? a.den : null;
    case "sup":
      return slot === 0 ? a.base : slot === 1 ? a.exp : null;
    case "fn":
      return slot === 0 ? a.arg : null;
    case "logbase":
      return slot === 0 ? a.base : slot === 1 ? a.arg : null;
    default:
      return null;
  }
}

function withListAtPath(
  root: Atom[],
  path: string[],
  mut: (list: Atom[]) => Atom[],
): Atom[] {
  if (path.length === 0) return mut(root);
  const [first, ...rest] = path;
  const [id, slotStr] = first.split(":");
  const slot = slotStr === undefined ? null : (Number(slotStr) as 0 | 1);
  return root.map((a) => {
    if (a.id !== id) return a;
    return updateChild(a, slot, (l) => withListAtPath(l, rest, mut));
  });
}

function updateChild(
  a: Atom,
  slot: 0 | 1 | null,
  mut: (list: Atom[]) => Atom[],
): Atom {
  switch (a.kind) {
    case "paren":
    case "abs":
    case "sqrt":
      return slot === null ? { ...a, inner: mut(a.inner) } : a;
    case "nthroot":
      return slot === 0
        ? { ...a, degree: mut(a.degree) }
        : slot === 1
          ? { ...a, inner: mut(a.inner) }
          : a;
    case "frac":
      return slot === 0
        ? { ...a, num: mut(a.num) }
        : slot === 1
          ? { ...a, den: mut(a.den) }
          : a;
    case "sup":
      return slot === 0
        ? { ...a, base: mut(a.base) }
        : slot === 1
          ? { ...a, exp: mut(a.exp) }
          : a;
    case "fn":
      return slot === 0 ? { ...a, arg: mut(a.arg) } : a;
    case "logbase":
      return slot === 0
        ? { ...a, base: mut(a.base) }
        : slot === 1
          ? { ...a, arg: mut(a.arg) }
          : a;
    default:
      return a;
  }
}

interface Props {
  ma: MaId | null;
  /** When true, a window-level keydown listener is attached so the user can
   *  type from the physical keyboard. Activation logic lives in the parent
   *  (CalculatorOverlay) — hover/click/drag on the calculator. */
  keyboardActive?: boolean;
}

export default function Calculator({ ma, keyboardActive }: Props) {
  const [state, dispatch] = useReducer(reducer, undefined, initial);
  const { calcLocked: locked, multiplicationTableEnabled } = useToolLock();
  // view: "calc" (räknaren) eller "tabell" (multiplikationstabell). Tabellen är
  // alltid tillgänglig — även när uppgiften låser räknaren — så den lyfts till
  // context så overlayn kan släppa "låst"-tinten medan tabellen visas.
  const { view, setView } = useCalculator();
  // If the class disables the multiplication table while it's showing, fall
  // back to the calculator face so the user isn't stuck on a hidden tab.
  useEffect(() => {
    if (!multiplicationTableEnabled && view === "tabell") setView("calc");
  }, [multiplicationTableEnabled, view, setView]);
  // Två lägen: Ma 1-2 (enkel — utan logaritmer/Eulers tal) och Ma 3-5
  // (avancerad — med lg/ln/logₐ/e/eʸ + n!/1/x/|x|/10ˣ). Toggle-knapparna
  // syns alltid så eleven kan välja oavsett kurs. Default sätts från
  // ma-prop:en (Ma 3-5 → avancerad, annars enkel).
  const isMa3Plus = ma === "ma3" || ma === "ma4" || ma === "ma5";
  // Nivån härleds ur elevens aktiva kurs så länge eleven inte valt själv.
  // Härledd (inte kopierad till state) eftersom `ma` resolvas asynkront i
  // overlayn — den är null i första rendret och landar först när kursen är
  // känd. Ett eget val bevaras: en Ma1-elev som medvetet tagit fram
  // logaritmerna ska inte få dem borttagna vid nästa uppgift.
  const [levelOverride, setLevelOverride] = useState<
    "enkel" | "avancerad" | null
  >(null);
  const level: "enkel" | "avancerad" =
    levelOverride ?? (isMa3Plus ? "avancerad" : "enkel");
  const showLogs = level === "avancerad";

  // Låset styr vilken vy fönstret visar. Låses uppgiften (digitala verktyg
  // ej tillåtna) byter vi till multiplikationstabellen — den är ett
  // uppslagsstöd, inte ett verktyg. Släpps låset (uppgiften tillåter
  // verktyg, eller läraren tillåter miniräknare på alla uppgifter) går vi
  // tillbaka till räknaren, i den nivå elevens aktiva kurs kräver.
  // Bara *övergångar* styr vyn: annars gick det inte att välja tabellen
  // frivilligt på en olåst uppgift. Saknas tabellen (läraren har dolt den)
  // ligger vyn kvar på räknaren med sin lås-overlay.
  const prevLockedRef = useRef(false);
  useEffect(() => {
    const prev = prevLockedRef.current;
    prevLockedRef.current = locked;
    if (prev === locked) return;
    if (locked) {
      if (multiplicationTableEnabled) setView("tabell");
      return;
    }
    setView("calc");
  }, [locked, multiplicationTableEnabled, setView]);

  // Kort meddelande när eleven klickar på en räknar-flik under lås. Vyn
  // ändras inte — multiplikationstabellen ligger kvar.
  const [lockMsg, setLockMsg] = useState(false);
  const lockMsgTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (lockMsgTimer.current !== null) window.clearTimeout(lockMsgTimer.current);
    },
    [],
  );
  const showLockMsg = () => {
    setLockMsg(true);
    if (lockMsgTimer.current !== null) window.clearTimeout(lockMsgTimer.current);
    lockMsgTimer.current = window.setTimeout(() => setLockMsg(false), 2600);
  };

  // Keyboard listener — only active when parent says so AND not locked.
  // Skip events whose target is an input/textarea/contenteditable so user
  // can keep typing in other fields even while hovering the calculator.
  useEffect(() => {
    if (!keyboardActive || locked || view !== "calc") return;
    function onKey(e: KeyboardEvent) {
      const t = e.target as Element | null;
      if (t) {
        const tag = t.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        if (t instanceof HTMLElement && t.isContentEditable) return;
      }
      const k = e.key;
      if (k === "ArrowLeft") {
        e.preventDefault();
        dispatch({ type: "arrow-left" });
      } else if (k === "ArrowRight") {
        e.preventDefault();
        dispatch({ type: "arrow-right" });
      } else if (k >= "0" && k <= "9") {
        e.preventDefault();
        dispatch({ type: "digit", v: k });
      } else if (k === "+") {
        e.preventDefault();
        dispatch({ type: "op", v: "+" });
      } else if (k === "-") {
        e.preventDefault();
        dispatch({ type: "op", v: "-" });
      } else if (k === "*") {
        e.preventDefault();
        dispatch({ type: "op", v: "*" });
      } else if (k === "/" || k === "%") {
        e.preventDefault();
        dispatch({ type: "op", v: "/" });
      } else if (k === "^") {
        e.preventDefault();
        dispatch({ type: "sup" });
      } else if (k === "(") {
        e.preventDefault();
        dispatch({ type: "lparen" });
      } else if (k === "," || k === ".") {
        e.preventDefault();
        dispatch({ type: "decimal" });
      } else if (k === "Enter" || k === "=") {
        e.preventDefault();
        dispatch({ type: "equals" });
      } else if (k === "Backspace") {
        e.preventDefault();
        // Shift+Backspace = rensa allt; vanlig Backspace = radera ett steg
        dispatch({ type: e.shiftKey ? "clear" : "backspace" });
      } else if (k === "Delete") {
        e.preventDefault();
        dispatch({ type: "clear" });
      } else if (k === "t") {
        e.preventDefault();
        dispatch({ type: "fn", name: "tan" });
      } else if (k === "T") {
        e.preventDefault();
        dispatch({ type: "fn", name: "atan" });
      } else if (k === "s") {
        e.preventDefault();
        dispatch({ type: "fn", name: "sin" });
      } else if (k === "S") {
        e.preventDefault();
        dispatch({ type: "fn", name: "asin" });
      } else if (k === "c") {
        e.preventDefault();
        dispatch({ type: "fn", name: "cos" });
      } else if (k === "C") {
        e.preventDefault();
        dispatch({ type: "fn", name: "acos" });
      } else if (k === "p" || k === "P") {
        e.preventDefault();
        dispatch({ type: "const", name: "pi" });
      } else if (k === "r") {
        e.preventDefault();
        dispatch({ type: "sqrt" });
      } else if (k === "R") {
        e.preventDefault();
        dispatch({ type: "nthroot" });
      } else if (k === "y") {
        e.preventDefault();
        dispatch({ type: "sup" });
      } else if (k === "Y") {
        e.preventDefault();
        dispatch({ type: "square" });
      } else if (k === "e" || k === "E") {
        e.preventDefault();
        dispatch({ type: "const", name: "e" });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboardActive, locked, view]);

  const onCursorTo = (cursor: Cursor) => dispatch({ type: "cursor", cursor });

  return (
    <div
      className="w-full max-w-md mx-auto px-3 py-4 select-none relative"
      style={{
        // Lås gäller bara räknar-läget. I tabell-läget är allt klickbart även
        // om uppgiften låser räknaren — tabellen är ett uppslagsstöd, inte ett
        // verktyg. Flik-raden nedan punktmarkeras med pointerEvents:"auto" så
        // man alltid kan växla tillbaka.
        pointerEvents: locked && view === "calc" ? "none" : undefined,
      }}
      aria-disabled={locked && view === "calc"}
    >
      {locked && view === "calc" && (
        <div
          aria-live="polite"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(80, 80, 80, 0.55)",
            color: "white",
            fontWeight: 600,
            fontSize: "0.95rem",
            textAlign: "center",
            padding: "0 1.5rem",
            borderRadius: "0.5rem",
            zIndex: 10,
            pointerEvents: "none",
            textShadow: "0 1px 3px rgba(0, 0, 0, 0.6)",
          }}
        >
          Miniräknaren är inte tillåten på den här uppgiften
        </div>
      )}
      {lockMsg && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 1.5rem",
            zIndex: 30,
            pointerEvents: "none",
          }}
        >
          {/* Samma tema-neutrala grå som lås-overlayn ovan — "låst"-signalen
              ska se likadan ut i alla teman. */}
          <span
            style={{
              background: "rgba(80, 80, 80, 0.92)",
              color: "white",
              fontWeight: 600,
              fontSize: "0.95rem",
              textAlign: "center",
              padding: "0.6rem 1rem",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
            }}
          >
            Miniräknaren är inte tillåten på den här uppgiften
          </span>
        </div>
      )}
      {/* Display — fast höjd, innehåll auto-skalas så det aldrig växer. Döljs i
          tabell-läget (räknaren ersätts då helt av multiplikationstabellen). */}
      {view === "calc" && (
        <DisplayBox
          expr={state.expr}
          cursor={state.cursor}
          onCursorTo={onCursorTo}
          result={state.result}
          error={state.error}
          onClickEnd={() =>
            dispatch({ type: "cursor", cursor: cursorAtEnd(state.expr) })
          }
        />
      )}

      {/* Läges-flikar: DEG till vänster, mapp-tabbar till höger.
          [multipl. tabell] [ma1-2] [ma forts. & fördj.]. Raden ligger över
          ev. lås-overlay (zIndex) och re-aktiverar pointer events så man alltid
          kan växla läge — även när räknaren är låst. */}
      <div
        className="flex items-end mb-2 text-xs font-mono uppercase tracking-wider"
        style={{ position: "relative", zIndex: 20, pointerEvents: "auto" }}
      >
        <ModeBtn
          label={state.mode === "deg" ? "DEG" : "RAD"}
          active={false}
          onClick={() => dispatch({ type: "toggleMode" })}
          title={`Växla mellan grader och radianer (nu: ${state.mode === "deg" ? "grader" : "radianer"})`}
        />
        <div className="ml-auto flex items-end gap-1 flex-wrap justify-end">
          {multiplicationTableEnabled && (
            <FolderTab
              label="multipl. tabell"
              active={view === "tabell"}
              onClick={() => setView("tabell")}
              title="Multiplikationstabell — uppslagsstöd 2–9 × 2–9"
            />
          )}
          <FolderTab
            label="ma1-2"
            active={view === "calc" && level === "enkel"}
            onClick={() => {
              if (locked) {
                showLockMsg();
                return;
              }
              setView("calc");
              setLevelOverride("enkel");
            }}
            title={
              locked
                ? "Miniräknaren är inte tillåten på den här uppgiften"
                : "ma1-2-räknare — döljer logaritmer och Eulers tal"
            }
          />
          <FolderTab
            label="ma forts. & fördj."
            active={view === "calc" && level === "avancerad"}
            onClick={() => {
              if (locked) {
                showLockMsg();
                return;
              }
              setView("calc");
              setLevelOverride("avancerad");
            }}
            title={
              locked
                ? "Miniräknaren är inte tillåten på den här uppgiften"
                : "ma forts. & fördj.-räknare — visar logaritmer (lg, ln, logₐ), Eulers tal och fler funktioner"
            }
          />
        </div>
      </div>

      {view === "tabell" ? (
        <MultiplicationTable />
      ) : (
      <>
      {/* Funktions-grid — kol 1-5 är gemensamma för Ma 1-2 och Ma 3-5.
          Ma 3-5 har en extra 6:e kolumn med kombinatorik-funktioner och |x|. */}
      <div className={`grid ${showLogs ? "grid-cols-6" : "grid-cols-5"} gap-1.5 mb-6`}>
        {/* Rad 1: ← → ( ) a/b eʸ + |x| (Ma 3-5) */}
        <Btn label="←" tone="fn" onClick={() => dispatch({ type: "arrow-left" })} title="Flytta markör vänster" />
        <Btn label="→" tone="fn" onClick={() => dispatch({ type: "arrow-right" })} title="Flytta markör höger" />
        <Btn label="(    )" tone="fn" onClick={() => dispatch({ type: "lparen" })} title="Parentes — markören placeras inuti" />
        <Btn label="a/b" tone="fn" onClick={() => dispatch({ type: "frac" })} title="Bråk" />
        <Btn label="π" tone="fn" onClick={() => dispatch({ type: "const", name: "pi" })} title="Pi — tangent: p" />
        {showLogs && (
          <Btn label="|x|" tone="fn" onClick={() => dispatch({ type: "abs" })} title="Absolutbelopp" />
        )}

        {/* Rad 2: sin cos tan xʸ x² + n! (Ma 3-5) */}
        <Btn label="sin" tone="fn" onClick={() => dispatch({ type: "fn", name: "sin" })} title="Sinus — tangent: s" />
        <Btn label="cos" tone="fn" onClick={() => dispatch({ type: "fn", name: "cos" })} title="Cosinus — tangent: c" />
        <Btn label="tan" tone="fn" onClick={() => dispatch({ type: "fn", name: "tan" })} title="Tangens — tangent: t" />
        <Btn label="xʸ" tone="fn" onClick={() => dispatch({ type: "sup" })} title="Allmän potens — tangent: y" />
        <Btn label="x²" tone="fn" onClick={() => dispatch({ type: "square" })} title="Kvadrat — tangent: Shift+Y" />
        {showLogs && (
          <Btn label="n!" tone="fn" onClick={() => dispatch({ type: "fact" })} title="Fakultet — n! = 1·2·…·n" />
        )}

        {/* Rad 3: sin⁻¹ cos⁻¹ tan⁻¹ √ ⁿ√ + nCr (Ma 3-5) */}
        <Btn label="sin⁻¹" tone="fn" onClick={() => dispatch({ type: "fn", name: "asin" })} title="Arcus sinus — tangent: Shift+S" />
        <Btn label="cos⁻¹" tone="fn" onClick={() => dispatch({ type: "fn", name: "acos" })} title="Arcus cosinus — tangent: Shift+C" />
        <Btn label="tan⁻¹" tone="fn" onClick={() => dispatch({ type: "fn", name: "atan" })} title="Arcus tangens — tangent: Shift+T" />
        <Btn label="√" tone="fn" onClick={() => dispatch({ type: "sqrt" })} title="Kvadratrot — tangent: r" />
        <Btn label="ⁿ√" tone="fn" onClick={() => dispatch({ type: "nthroot" })} title="N:te roten — tangent: Shift+R" />
        {showLogs && (
          <Btn label="nCr" tone="fn" onClick={() => dispatch({ type: "ncr" })} title="Binomialkoefficient — antal sätt att välja r av n (Ma 5)" />
        )}

        {/* Rad 4: e eʸ lg ln logₐ + nPr (Ma 3-5) */}
        <Btn label="e" tone="fn" onClick={() => dispatch({ type: "const", name: "e" })} title="Eulers tal e — tangent: e" />
        <Btn label="eʸ" tone="fn" onClick={() => dispatch({ type: "expY" })} title="e upphöjt till y — fyll i exponenten" />
        <Btn label="lg" tone="fn" onClick={() => dispatch({ type: "fn", name: "lg" })} title="Tiologaritm — log med bas 10" />
        <Btn label="ln" tone="fn" onClick={() => dispatch({ type: "fn", name: "ln" })} title="Naturlig logaritm — log med bas e" />
        <Btn label="logₐ" tone="fn" onClick={() => dispatch({ type: "logbase" })} title="Logaritm med valfri bas — fyll i bas och argument" />
        {showLogs && (
          <Btn label="nPr" tone="fn" onClick={() => dispatch({ type: "npr" })} title="Permutationer — antal ordnade urval av r ur n (Ma 5)" />
        )}
      </div>


      {/* Siffer-grid — kol 5 har ⌫ (rad 1) och C (rad 2) i alla lägen */}
      <div className="grid grid-cols-5 gap-1.5">
        <Btn label="7" onClick={() => dispatch({ type: "digit", v: "7" })} />
        <Btn label="8" onClick={() => dispatch({ type: "digit", v: "8" })} />
        <Btn label="9" onClick={() => dispatch({ type: "digit", v: "9" })} />
        <Btn label="÷" tone="op" onClick={() => dispatch({ type: "op", v: "/" })} title="Division" />
        <Btn label="⌫" tone="fn" onClick={() => dispatch({ type: "backspace" })} title="Sudda en symbol — tangent: Backspace" />

        <Btn label="4" onClick={() => dispatch({ type: "digit", v: "4" })} />
        <Btn label="5" onClick={() => dispatch({ type: "digit", v: "5" })} />
        <Btn label="6" onClick={() => dispatch({ type: "digit", v: "6" })} />
        <Btn label="×" tone="op" onClick={() => dispatch({ type: "op", v: "*" })} title="Multiplikation" />
        <Btn label="C" tone="warn" onClick={() => dispatch({ type: "clear" })} title="Rensa allt — tangent: Delete eller Shift+Backspace" />

        <Btn label="1" onClick={() => dispatch({ type: "digit", v: "1" })} />
        <Btn label="2" onClick={() => dispatch({ type: "digit", v: "2" })} />
        <Btn label="3" onClick={() => dispatch({ type: "digit", v: "3" })} />
        <Btn label="−" tone="op" onClick={() => dispatch({ type: "op", v: "-" })} title="Subtraktion" />
        <span />

        <Btn label="," tone="fn" onClick={() => dispatch({ type: "decimal" })} title="Decimaltecken — tangent: , eller ." />
        <Btn label="0" onClick={() => dispatch({ type: "digit", v: "0" })} colSpan={2} />
        <Btn label="+" tone="op" onClick={() => dispatch({ type: "op", v: "+" })} title="Addition" />
        <Btn label="=" tone="primary" onClick={() => dispatch({ type: "equals" })} title="Beräkna" />
      </div>
      </>
      )}
    </div>
  );
}

function DisplayBox({
  expr,
  cursor,
  onCursorTo,
  result,
  error,
  onClickEnd,
}: {
  expr: Atom[];
  cursor: Cursor;
  onCursorTo: (c: Cursor) => void;
  result: string | null;
  error: string | null;
  onClickEnd: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const exprRef = useRef<HTMLDivElement>(null);
  const [exprScale, setExprScale] = useState(1);

  // När uttrycket ändras, mät innehåll mot tillgänglig yta och skala ner
  // om det skulle överflöda. Gör aldrig display större — den har fast höjd.
  useLayoutEffect(() => {
    if (!containerRef.current || !exprRef.current) return;
    // Mät i ren form (scale 1) först
    exprRef.current.style.transform = "scale(1)";
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const ew = exprRef.current.scrollWidth;
    const eh = exprRef.current.scrollHeight;
    if (ew === 0 || eh === 0) return;
    const s = Math.min(1, cw / ew, ch / eh);
    setExprScale(Math.max(0.4, s));
  }, [expr, result, error]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-md border bg-[var(--color-bg-card)] px-4 pt-3 pb-2 mb-3 h-[6.3rem] text-[var(--color-text)] overflow-hidden flex flex-col"
      style={{ borderColor: "var(--color-border)" }}
      onClick={onClickEnd}
    >
      {/* Uttrycks-rad — flexar och fyller övre delen av display, höger-justerad */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 flex items-start justify-end overflow-hidden"
      >
        <div
          ref={exprRef}
          className="text-2xl text-right whitespace-nowrap"
          style={{
            transform: `scale(${exprScale})`,
            transformOrigin: "top right",
          }}
        >
          {expr.length === 0 ? (
            <span className="text-[var(--color-text-muted)]">0</span>
          ) : (
            <RenderList list={expr} path={[]} ctx={{ cursor, onCursorTo }} />
          )}
        </div>
      </div>
      {/* Svar-rad — fast längst ner, ej skalad */}
      <div
        className="text-right text-base flex-shrink-0"
        style={{
          color: error
            ? "var(--color-figur-rost)"
            : "var(--color-figur-mossa)",
          minHeight: "1.25rem",
        }}
      >
        {error ? `Fel: ${error}` : result !== null ? `= ${result}` : ""}
      </div>
    </div>
  );
}

function ModeBtn({
  label,
  active,
  onClick,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="px-2 py-1 rounded border transition-colors"
      style={{
        borderColor: active ? "var(--color-figur-mossa)" : "var(--color-border)",
        color: active ? "var(--color-figur-mossa)" : "var(--color-text-muted)",
        borderWidth: active ? 2 : 1,
      }}
    >
      {label}
    </button>
  );
}

// Kompakt mapp-tab — tunn mörkgrå ram + text. Aktiv fliken har bottom-borderen
// undertryckt så den läses som ett "kort" framför grid:en nedanför.
function FolderTab({
  label,
  active,
  onClick,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="px-2 py-0.5 transition-colors"
      style={{
        border: "1px solid #555",
        borderTopLeftRadius: "0.25rem",
        borderTopRightRadius: "0.25rem",
        color: active ? "#222" : "#888",
        background: active ? "var(--color-bg-card)" : "transparent",
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

interface BtnProps {
  label: string;
  onClick: () => void;
  tone?: "primary" | "op" | "fn" | "warn";
  colSpan?: number;
  rowSpan?: number;
  title?: string;
}

function Btn({ label, onClick, tone, colSpan, rowSpan, title }: BtnProps) {
  // Knapp-content (siffror/symboler/ikoner) ökat ~15 % mot tidigare för bättre
  // läsbarhet. Default: 1rem → 1.15rem. Fn-knappar: 0.85rem → 0.98rem.
  const base =
    "min-h-12 rounded-md border font-medium transition-colors active:scale-[0.97]";
  const span =
    `${colSpan ? `col-span-${colSpan}` : ""} ${rowSpan ? `row-span-${rowSpan}` : ""}`.trim();
  let style: React.CSSProperties = {};
  switch (tone) {
    case "primary":
      style = {
        background: "var(--color-figur-mossa)",
        borderColor: "var(--color-figur-mossa)",
        color: "white",
        fontSize: "1.15rem",
      };
      break;
    case "op":
      style = {
        background: "var(--color-bg-card)",
        borderColor: "var(--color-border)",
        color: "var(--color-accent)",
        fontSize: "1.15rem",
      };
      break;
    case "fn":
      style = {
        background: "var(--color-bg-card)",
        borderColor: "var(--color-border)",
        color: "var(--color-text-muted)",
        fontSize: "0.98rem",
      };
      break;
    case "warn":
      style = {
        background: "var(--color-bg-card)",
        borderColor: "var(--color-figur-rost)",
        color: "var(--color-figur-rost)",
        fontSize: "1.15rem",
      };
      break;
    default:
      style = {
        background: "var(--color-bg-card)",
        borderColor: "var(--color-border)",
        color: "var(--color-text)",
        fontSize: "1.15rem",
      };
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${span}`.trim()}
      style={style}
      title={title || label}
    >
      {label}
    </button>
  );
}
